import React from 'react';
import { CheckIcon, MapPinIcon } from 'lucide-react';
import type { Profile, TextSegment } from '../data/profiles';
type ProfileCardProps = {
  profile: Profile;
  dark?: boolean;
};
export function ProfileCard({
  profile,
  dark = false
}: ProfileCardProps) {
  const Icon = profile.icon;
  const strongClass = dark ? 'font-semibold text-white' : 'font-semibold text-ink';
  return <article aria-labelledby={`profile-${profile.id}`} className={`flex flex-col rounded-3xl p-5 sm:p-7 ${dark ? 'bg-brand-950 text-white' : 'bg-mist text-ink ring-1 ring-brand-100'}`} data-id="element-129" data-mp="2w8mzw786bjnt">
      <div className="flex items-center gap-3" data-id="element-130" data-mp="2n88dt5ndy0j9">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${dark ? 'bg-sun text-ink' : 'bg-brand-600 text-white'}`} data-id="element-131" data-mp="2n9jgf253h28s">
          <Icon className="h-6 w-6" aria-hidden="true" data-id="element-132" data-mp="lfsl4hje66bi" />
        </span>
        <div className="min-w-0" data-id="element-133" data-mp="lehiil1on4lz">
          <p className={`font-display text-lg font-extrabold uppercase leading-tight tracking-tight sm:text-xl ${dark ? 'text-white' : 'text-ink'}`} data-id="element-134" data-mp="qfop4gs39xr2">
            “{profile.cardLabel}”
          </p>
          <p className={`mt-0.5 text-xs font-bold uppercase tracking-wider ${dark ? 'text-sun' : 'text-brand-600'}`} data-id="element-135" data-mp="25oqnzjepv8hl">{profile.tag}</p>
        </div>
      </div>

      <h3 id={`profile-${profile.id}`} className={`mt-5 font-display text-[22px] font-extrabold uppercase leading-tight tracking-tight sm:text-[26px] ${dark ? 'text-white' : 'text-ink'}`} data-id="element-136" data-mp="2jhq7rxlj7ouy">
        {profile.title.map((s, i) => <span key={i} className={s.strong ? dark ? 'text-sun' : 'text-brand-600' : undefined} data-id="element-137" data-mp="1mqeetxnt1ym0" data-mpi={i}>
            {s.text}
          </span>)}
      </h3>

      <div className={`mt-3 space-y-2 text-[15px] leading-relaxed ${dark ? 'text-brand-100' : 'text-ink-soft'}`} data-id="element-138" data-mp="1vq16hrqqfq1v">
        {profile.paragraphs.map((p, i) => <p key={i} data-id="element-139" data-mp="3c2q1v0ob63wy" data-mpi={i}>{renderSegments(p, strongClass)}</p>)}
      </div>

      {profile.lists.map(list => <div key={list.title} className="mt-6" data-id="element-140" data-mp="377anm7me2600">
          <h4 className={`font-display text-sm font-extrabold uppercase tracking-wider ${dark ? 'text-white' : 'text-ink'}`} data-id="element-141" data-mp="3f6noyg4s4e7n">{list.title}</h4>
          {list.style === 'checks' ? <ul className="mt-3 space-y-2" data-id="element-142" data-mp="3o6u3848axi7v">
              {list.items.map(item => <li key={item.text} className="flex items-start gap-2.5 text-[14px] leading-snug sm:text-[15px]" data-id="element-143" data-mp="3t5nia138lha5">
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${dark ? 'bg-brand-600 text-white' : 'bg-brand-600 text-white'}`} data-id="element-144" data-mp="2viexy9ez2e9q">
                    <CheckIcon className="h-3 w-3" strokeWidth={3.5} aria-hidden="true" data-id="element-145" data-mp="35dzu3qxd0kbk" />
                  </span>
                  <span className={item.highlight ? dark ? 'font-bold text-sun' : 'font-bold text-brand-700' : dark ? 'text-brand-100' : 'text-ink'} data-id="element-146" data-mp="3a8may9m7n531">
                    {item.text}
                  </span>
                </li>)}
            </ul> : <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-147" data-mp="1v9qblkv8qcm4">
              {list.items.map(item => <li key={item.text} className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold text-ink ring-1 ring-brand-100" data-id="element-148" data-mp="1r7v2vvazcwyb">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" data-id="element-149" data-mp="1hhilgbygpjwe" />
                  {item.text}
                </li>)}
            </ul>}
        </div>)}

      <div className="mt-auto pt-6" data-id="element-150" data-mp="17j2ss4jse2ks">
        <blockquote className={`rounded-2xl p-4 ${dark ? 'bg-white/5 ring-1 ring-white/10' : 'bg-brand-600 text-white'}`} data-id="element-151" data-mp="17m8fxe8uuudv">
          <p className={`font-display text-base font-extrabold uppercase leading-snug ${dark ? 'text-sun' : 'text-white'}`} data-id="element-152" data-mp="1s9ggs956zyuw">{profile.quote[0]}</p>
          <p className={`mt-1 font-display text-[15px] font-bold leading-snug ${dark ? 'text-white' : 'text-brand-50'}`} data-id="element-153" data-mp="1x6yjhhsm9hug">{profile.quote[1]}</p>
        </blockquote>

        <div className="mt-4" data-id="element-154" data-mp="1nhyenhrfns8c">
          {profile.footnoteTitle && <p className={`font-display text-sm font-extrabold uppercase tracking-wider ${dark ? 'text-white' : 'text-ink'}`} data-id="element-155" data-mp="1ng3u44k2q24s">
              {profile.footnoteTitle}
            </p>}
          <p className={`mt-1 text-[13px] leading-relaxed ${dark ? 'text-brand-100' : 'text-ink-soft'}`} data-id="element-156" data-mp="1efp8t5740n1a">{profile.footnote}</p>
        </div>
      </div>
    </article>;
}
function renderSegments(segments: TextSegment[], strongClass: string) {
  return segments.map((s, i) => s.strong ? <strong key={i} className={strongClass} data-id="element-157" data-mp="2utxfrrfecnrt">
        {s.text}
      </strong> : <React.Fragment key={i} data-id="element-158" data-mp="2ywu3txnphc35">{s.text}</React.Fragment>);
}